import { useCallback, useContext, useMemo } from "react";
import {
  RosebudRouteSetting,
  RosebudRoutesSettings,
} from "./util/rosebudRoutesSettings";
import { PatientCaseGrouping } from "../../context/diagnosticAgent";
import { KahunPanelSubGroup } from "shared";
import { useHistory, useRouteMatch } from "react-router";
import { useScreenSize } from "./util/useScreenSize";
import { AgentRoute } from "./util/agentRoutes";
import {
  getCurrentAgentRoute,
  removeLastAgentRoute,
} from "../../util/pathUtils";
import _ from "lodash";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { SearchQueriesContext } from "../../context/searchQuery";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";

type GoToStepProps = {
  step?: RosebudRouteSetting;
  route?: AgentRoute | string;
  subGroupId?: string;
  grouping?: PatientCaseGrouping;
  panelId?: string;
  /***
   * By default search params are moved around, if set to true,
   * they will be cleared them when navigating
   * (we use it to remove sharedId for example when clicking start over)
   */
  clearSearch?: boolean;
};

export const useWizardSteps = () => {
  // Filter out mobile routes (if needed)
  const { isMobile } = useScreenSize();
  const { agreeOnTerms } = useContext(SearchQueriesContext);

  // Skip user agreement if agreed on terms
  const shouldSkipUserAgreement = useCallback(
    (setting: RosebudRouteSetting) =>
      (agreeOnTerms && setting.route !== AgentRoute.TermsOfService) ||
      !agreeOnTerms,
    [agreeOnTerms],
  );

  const mobileSteps = useMemo(
    () =>
      RosebudRoutesSettings.filter(
        (s) => s.isWizardStep && !s.desktopOnly && shouldSkipUserAgreement(s),
      ),
    [shouldSkipUserAgreement],
  );

  const desktopSteps = useMemo(
    () =>
      RosebudRoutesSettings.filter(
        (s) => s.isWizardStep && !s.mobileOnly && shouldSkipUserAgreement(s),
      ),
    [shouldSkipUserAgreement],
  );

  const wizardSteps = useMemo(() => (isMobile ? mobileSteps : desktopSteps), [
    isMobile,
    mobileSteps,
    desktopSteps,
  ]);

  return { wizardSteps };
};

export const useNavigationSteps = () => {
  // Filter out mobile routes (if needed)
  const { isMobile } = useScreenSize();

  const mobileSteps = useMemo(
    () =>
      RosebudRoutesSettings.filter(
        (s) =>
          (s.isWizardStep && !s.desktopOnly) ||
          s.renderBottomNavigation?.({ isMobile: true, inWizard: false }),
      ),
    [],
  );

  const desktopSteps = useMemo(
    () =>
      RosebudRoutesSettings.filter(
        (s) =>
          (s.isWizardStep && !s.mobileOnly) ||
          s.renderBottomNavigation?.({ isMobile: false, inWizard: false }),
      ),
    [],
  );

  const navigationSteps = useMemo(
    () => (isMobile ? mobileSteps : desktopSteps),
    [isMobile, mobileSteps, desktopSteps],
  );

  return { navigationSteps };
};

export const useRouteHelper = () => {
  const history = useHistory();
  const { isMobile } = useScreenSize();
  const match = useRouteMatch();
  const { inWizard } = useDiagnosticNavigation();
  const { wizardSteps } = useWizardSteps();
  const newWizard = getFeatureFlags("newWizard");

  // modify grouping to mobile
  const caseGroupoingUrlByDevice = useCallback(
    (grouping: PatientCaseGrouping) => {
      if (
        !newWizard &&
        isMobile &&
        (grouping === PatientCaseGrouping.WORKUP ||
          grouping === PatientCaseGrouping.BACKGROUND ||
          grouping === PatientCaseGrouping.CLINICAL_FEATURES)
      ) {
        if (!inWizard) {
          return AgentRoute.Edit + _.upperFirst(grouping);
        } else {
          return AgentRoute.DataEntry + "/" + grouping;
        }
      }
      return grouping;
    },
    [isMobile, inWizard, newWizard],
  );

  const matchUrl = match?.url;

  const navigateTo = useCallback(
    (goToProps: GoToStepProps) => {
      let urlToPush = removeLastAgentRoute(matchUrl);

      if (!goToProps.route && goToProps.step) {
        goToProps.route = goToProps.step.route as AgentRoute;
      }

      if (goToProps.grouping) {
        urlToPush += `/${caseGroupoingUrlByDevice(goToProps.grouping)}`;
      } else if (goToProps.route) {
        urlToPush += `/${goToProps.route}`;
      }

      if (goToProps.subGroupId) {
        const subGroupKey = Object.keys(KahunPanelSubGroup).find(
          (key) => KahunPanelSubGroup[key] === goToProps.subGroupId,
        );
        if (subGroupKey) {
          urlToPush += `/${subGroupKey}`;
        }
      }

      if (goToProps.panelId && goToProps.subGroupId) {
        urlToPush += `/${goToProps.panelId}`;
      }

      const { clearSearch } = goToProps;
      history.push({
        pathname: urlToPush,
        search: clearSearch ? "" : history.location.search,
      });
    },
    [history, matchUrl, caseGroupoingUrlByDevice],
  );

  const currentWizardIndex = useMemo(() => {
    const route = getCurrentAgentRoute(matchUrl);
    return wizardSteps.findIndex((step) => step.route === route);
  }, [wizardSteps, matchUrl]);

  const cleanRoute = (route: string) => {
    return route.split("/")[0];
  };

  const routeActions = useMemo(
    () => ({
      navigateTo,
      getCurrentRouteSettings: () => {
        const route = getCurrentAgentRoute(matchUrl);
        return RosebudRoutesSettings.find(
          (routeSetting) => cleanRoute(routeSetting.route) === route,
        );
      },
      getNextRoute: () => {
        const route = getCurrentAgentRoute(matchUrl);
        const currentRouteIndex = wizardSteps.findIndex(
          (setting) => setting.route === route,
        );
        if (
          !currentRouteIndex &&
          currentRouteIndex !== 0 &&
          currentRouteIndex < wizardSteps.length
        ) {
          return RosebudRoutesSettings.find(
            (stetting) => stetting.route === AgentRoute.DD,
          );
        }
        return wizardSteps[currentRouteIndex + 1];
      },
      goToHome: () => {
        navigateTo({
          clearSearch: true, // Forget about the search params when navigating home
          route: AgentRoute.Welcome,
        });
      },
      goToLoginPage: () => {
        navigateTo({ route: AgentRoute.Login });
        // history.push({ pathname: "/login", search: history.location.search });
      },
      currentWizardIndex,
    }),
    [navigateTo, wizardSteps, matchUrl, currentWizardIndex],
  );

  return routeActions;
};
